import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Clock, Pencil, Play, Plus, SquareTerminal } from 'lucide-react';

import { CapacityMeta, CapacityWarning, useCapacity } from '../components/Capacity';
import { agentPrompt, curlSnippet, SDK_EXAMPLE } from '../components/code';
import { CopyAction, Empty, ErrorCallout, InlineConfirm, Lamp, PageHead, Segmented, SkeletonRows, Spinner, StatePill } from '../components/ui';
import { api, ApiError, asApiError } from '../lib/api';
import { runCommand } from '../lib/commands';
import { ago, duration, fullTime, inFuture, moneyFine, shortId, span } from '../lib/format';
import { useAction, useDocumentTitle, useNow, useResource } from '../lib/hooks';
import { useApiOrigin } from '../lib/plane-config';
import { useBilling, useSession } from '../lib/session';
import type { CommandResult, Sandbox, SandboxState, TeamDetail } from '../lib/types';
import { ReplayChip, ReplayLink } from './Replay';

const PAGE = 25;
const STATES: { value: SandboxState | 'all'; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'ready', label: 'Ready' },
  { value: 'closed', label: 'Closed' },
];

const CLOSE_REASON: Record<string, string> = {
  deleted: 'Deleted',
  expired: 'Timed out',
  idle: 'Idle',
  'box-lost': 'Box lost',
  'plane-restart': 'Plane restarted',
  'no-capacity': 'No capacity',
};

/** A list row's first cell: just the name. */
function SandboxName({ name, to }: { name: string; to?: string }) {
  return (
    <span className="sbx-name">
      {to ? (
        <Link className="sbx-name__name" to={to} onClick={(e) => e.stopPropagation()}>
          {name}
        </Link>
      ) : (
        <span className="sbx-name__name">{name}</span>
      )}
    </span>
  );
}

function NewSandbox() {
  const navigate = useNavigate();
  const { me } = useSession();
  const { reloadBilling } = useBilling();
  const verified = me?.team.verified ?? false;
  const create = useAction(async () => api<Sandbox>('/v1/sandboxes', { method: 'POST', body: { session: 'headless', timeoutMs: 30 * 60_000 } }));
  return (
    <>
      <button
        type="button"
        className="btn btn--primary"
        disabled={create.busy || !verified}
        aria-busy={create.busy}
        title={verified ? undefined : 'Open the link we emailed you first'}
        onClick={async () => {
          const s = await create.run();
          if (s) {
            void reloadBilling();
            navigate(`/sandboxes/${s.id}`);
          }
        }}
      >
        {create.busy ? <Spinner /> : <Plus aria-hidden="true" />}
        New sandbox
      </button>
      {create.error ? (
        <div style={{ flexBasis: '100%' }}>
          <ErrorCallout error={create.error} />
        </div>
      ) : null}
    </>
  );
}

function SandboxHead() {
  return (
    <thead>
      <tr>
        <th scope="col">Name</th>
        <th scope="col">State</th>
        <th scope="col">Created</th>
        <th scope="col" className="r">
          Cost
        </th>
      </tr>
    </thead>
  );
}

/** What the faded table behind the first-run card shows: made-up rows, so it reads as the list this becomes. */
const SAMPLE: { name: string; state: SandboxState; created: string; cost: string }[] = [
  { name: 'swift-otter', state: 'ready', created: '2m ago', cost: '$0.0260' },
  { name: 'ios-build', state: 'ready', created: '9m ago', cost: '$0.1170' },
  { name: 'calm-heron', state: 'pending', created: 'just now', cost: '$0.0000' },
  { name: 'nightly-tests', state: 'closed', created: '1h ago', cost: '$0.0520' },
  { name: 'golden-maple', state: 'closed', created: '3h ago', cost: '$0.1430' },
  { name: 'brisk-lynx', state: 'closed', created: '1d ago', cost: '$0.0130' },
];

type Start = 'prompt' | 'sdk' | 'curl';
const STARTS: { value: Start; label: string }[] = [
  { value: 'prompt', label: 'Prompt' },
  { value: 'sdk', label: 'TypeScript' },
  { value: 'curl', label: 'curl' },
];
const START_HINTS: Record<Start, ReactNode> = {
  prompt: 'Paste it into Claude Code, Codex, or any agent that runs a shell. It sets itself up.',
  sdk: (
    <>
      <code>Sandbox.create()</code> reads your key from <code>OFFSTAGE_API_KEY</code>. The install line is in the <Link to="/get-started">onboarding guide</Link>.
    </>
  ),
  curl: (
    <>
      Creates a sandbox and runs a command with your key in <code>OFFSTAGE_API_KEY</code>. <Link to="/keys">Create a key</Link>
    </>
  ),
};

/** An empty list's first run: start a Mac here, or copy a start for your agent. */
function FirstRun() {
  const origin = useApiOrigin();
  const { me } = useSession();
  const [start, setStart] = useState<Start>('prompt');
  const text = start === 'prompt' ? agentPrompt(origin) : start === 'sdk' ? SDK_EXAMPLE : curlSnippet(origin);
  return (
    <div className="firstrun">
      <div className="firstrun__ghost" aria-hidden="true">
        <table className="table">
          <SandboxHead />
          <tbody>
            {SAMPLE.map((s) => (
              <tr key={s.name}>
                <td className="m-title">
                  <SandboxName name={s.name} />
                </td>
                <td className="m-end">
                  <StatePill state={s.state} />
                </td>
                <td className="m-meta nowrap">{s.created}</td>
                <td className="r num m-meta">{s.cost}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="firstrun__card">
        <h2 className="firstrun__title">Run your first sandbox</h2>
        <p>A real Mac for your agent: one macOS account, ready in seconds, billed by the second.</p>
        <div className="row firstrun__actions">
          <NewSandbox />
        </div>
        {me && !me.team.verified ? <p className="firstrun__note">Open the link we emailed you first: sandboxes need a verified email.</p> : null}
        <div className="firstrun__or">
          or hand it to your agent
        </div>
        <div className="firstrun__strip">
          <Segmented label="Start from" options={STARTS} value={start} onChange={setStart} />
          <CopyAction text={text} label="Copy" size="sm" />
        </div>
        <p className="firstrun__hint">{START_HINTS[start]}</p>
      </div>
    </div>
  );
}

export function SandboxesPage() {
  useDocumentTitle('Sandboxes');
  const [params, setParams] = useSearchParams();
  const state = (params.get('state') as SandboxState | null) ?? 'all';
  const after = (params.get('after') ?? '').split(',').filter(Boolean);
  const cursor = after[after.length - 1] ?? null;
  const navigate = useNavigate();
  const now = useNow(1000);

  const path = `/v1/sandboxes?limit=${PAGE}${state !== 'all' ? `&state=${state}` : ''}${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''}`;
  const res = useResource<{ sandboxes: Sandbox[]; nextCursor: string | null }>(path, { interval: 4000 });
  // The concurrency limit counts every open sandbox: ready, and pending ones waiting for a slot.
  const ready = useResource<{ sandboxes: Sandbox[]; nextCursor: string | null }>('/v1/sandboxes?limit=200&state=ready', { interval: 8000 });
  const pending = useResource<{ sandboxes: Sandbox[]; nextCursor: string | null }>('/v1/sandboxes?limit=200&state=pending', { interval: 8000 });
  const capacity = useCapacity();
  const team = useResource<TeamDetail>('/v1/team');
  const list = res.data?.sandboxes ?? [];
  const openCount = ready.data && pending.data ? ready.data.sandboxes.length + pending.data.sandboxes.length : undefined;
  const cap = capacity.data;
  const limit = team.data?.limits.concurrentSandboxes;

  const setState = (value: SandboxState | 'all') => setParams(value === 'all' ? {} : { state: value });
  const go = (next: string[]) => {
    const p = new URLSearchParams(params);
    if (next.length) p.set('after', next.join(','));
    else p.delete('after');
    setParams(p);
  };

  return (
    <>
      <PageHead
        title="Sandboxes"
        actions={<NewSandbox />}
      />
      <div className="toolbar">
        <Segmented label="Filter by state" options={STATES} value={state} onChange={setState} />
        <span className="toolbar__meta">
          <CapacityMeta cap={cap} />
          {openCount !== undefined && limit !== undefined ? (
            <span className="live">
              <Lamp tone={openCount > 0 ? 'live' : 'off'} />
              {openCount} of {limit} open
            </span>
          ) : null}
        </span>
      </div>
      <CapacityWarning cap={cap} which="sandboxes" />
      {res.error ? (
        <div className="band">
          <ErrorCallout error={res.error} onRetry={() => void res.reload()} />
        </div>
      ) : null}
      <div className="band band--flush" style={{ paddingTop: 18 }}>
        {!res.loading && list.length === 0 && !res.error ? (
          state === 'all' && after.length === 0 ? (
            <FirstRun />
          ) : (
            <Empty title={`No ${state} sandboxes`}>
              <p>Nothing matches this filter.</p>
              <button type="button" className="btn" onClick={() => setState('all')}>
                Show all sandboxes
              </button>
            </Empty>
          )
        ) : (
          <div className="table-wrap">
            <table className="table">
              <SandboxHead />
              <tbody>
                {res.loading && !res.data ? (
                  <SkeletonRows cols={4} />
                ) : (
                  list.map((s) => (
                    <tr key={s.id} className="is-link" onClick={() => navigate(`/sandboxes/${s.id}`)}>
                      <td className="m-title">
                        <SandboxName name={s.name} to={`/sandboxes/${s.id}`} />
                        {s.replayUrl ? <ReplayChip id={s.id} live={s.state !== 'closed'} /> : null}
                      </td>
                      <td className="m-end">
                        <StatePill state={s.state} />
                      </td>
                      <td className="m-meta m-inline nowrap" title={fullTime(s.createdAt)}>
                        {ago(s.createdAt, now)}
                      </td>
                      <td className="r num m-meta" data-label="Cost">
                        {moneyFine(s.costUsd)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
        {(after.length > 0 || res.data?.nextCursor) && list.length > 0 ? (
          <div className="pager">
            <span>Page {after.length + 1}</span>
            <div className="pager__btns">
              <button type="button" className="btn btn--sm" disabled={after.length === 0} onClick={() => go(after.slice(0, -1))}>
                <ChevronLeft aria-hidden="true" />
                Newer
              </button>
              <button type="button" className="btn btn--sm" disabled={!res.data?.nextCursor} onClick={() => res.data?.nextCursor && go([...after, res.data.nextCursor])}>
                Older
                <ChevronRight aria-hidden="true" />
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </>
  );
}

/* ---------------------------------------------------------------- detail */

interface Entry {
  id: number;
  cmd: string;
  sentAt: number;
  /** Set once the plane answered 202: the command outlived the 50 s hold and is being followed. */
  following?: string;
  result?: CommandResult;
  error?: ApiError;
}

/** Commands worth a first click on a fresh Mac. */
const STARTERS = ['sw_vers', 'uname -m', 'xcodebuild -version', 'ls -la'];

function Running({ entry }: { entry: Entry }) {
  const now = useNow(1000);
  const elapsed = Math.max(0, now - entry.sentAt);
  return (
    <div className="sbx-term__running">
      <Spinner size={12} />
      <span>{entry.following ? `Still running, following ${shortId(entry.following)}` : 'Running'}</span>
      {elapsed >= 2000 ? <time>{duration(elapsed)}</time> : null}
    </div>
  );
}

function CommandConsole({ sandbox, onRan }: { sandbox: Sandbox; onRan: () => void }) {
  const [cmd, setCmd] = useState('');
  const [entries, setEntries] = useState<Entry[]>([]);
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState<string[]>([]);
  const [hIndex, setHIndex] = useState(-1);
  const out = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const nextId = useRef(1);
  const usable = sandbox.state !== 'closed';
  // Leaving the page stops following a long command (the command itself keeps running).
  const aborter = useRef<AbortController | null>(null);
  useEffect(() => () => aborter.current?.abort(), []);

  useEffect(() => {
    out.current?.scrollTo({ top: out.current.scrollHeight });
  }, [entries]);

  async function exec(command: string) {
    if (!command || busy || !usable) return;
    const id = nextId.current++;
    setEntries((prev) => [...prev, { id, cmd: command, sentAt: Date.now() }]);
    setHistory((h) => [command, ...h.filter((c) => c !== command)].slice(0, 50));
    setHIndex(-1);
    setCmd('');
    setBusy(true);
    aborter.current = new AbortController();
    try {
      const result = await runCommand(sandbox.id, { cmd: command }, {
        signal: aborter.current.signal,
        onRunning: (running) => setEntries((prev) => prev.map((x) => (x.id === id ? { ...x, following: running.commandId } : x))),
      });
      setEntries((prev) => prev.map((x) => (x.id === id ? { ...x, result } : x)));
    } catch (err) {
      if ((err as Error).name === 'AbortError') return;
      setEntries((prev) => prev.map((x) => (x.id === id ? { ...x, error: asApiError(err) } : x)));
    } finally {
      setBusy(false);
      onRan();
      requestAnimationFrame(() => input.current?.focus());
    }
  }

  function submit(e: FormEvent) {
    e.preventDefault();
    void exec(cmd.trim());
  }

  return (
    <section className="sbx-term" aria-label="Terminal">
      <div className="sbx-term__bar">
        <span className="sbx-term__title">
          <SquareTerminal aria-hidden="true" />
          Terminal
        </span>
        {entries.length > 0 ? (
          <button type="button" className="sbx-term__clear" onClick={() => setEntries([])}>
            Clear
          </button>
        ) : null}
      </div>
      <div className={`sbx-term__out${!usable && entries.length === 0 ? ' is-short' : ''}`} ref={out} aria-live="polite">
        {entries.length === 0 ? (
          <div className="sbx-term__empty">
            <p>
              {!usable
                ? 'This sandbox is closed, so it takes no more commands.'
                : sandbox.state === 'pending'
                  ? 'Waiting for a Mac. A command sent now waits up to 30 s for one.'
                  : 'Run any command on this Mac. Output comes back when it ends.'}
            </p>
            {usable ? (
              <div className="sbx-term__chips">
                {STARTERS.map((c) => (
                  <button key={c} type="button" className="sbx-term__chip" disabled={busy} onClick={() => void exec(c)}>
                    {c}
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        ) : (
          entries.map((entry) => (
            <div key={entry.id} className={`sbx-term__entry${entry.result || entry.error ? ' is-done' : ''}`}>
              <div className="sbx-term__cmd">
                <span className="sbx-term__ps" aria-hidden="true">
                  $
                </span>
                {entry.cmd}
              </div>
              {entry.result ? (
                <>
                  {entry.result.stdout || entry.result.stderr ? (
                    <pre className="sbx-term__pre">
                      {entry.result.stdout}
                      {entry.result.stderr ? <span className="sbx-term__err">{entry.result.stderr}</span> : null}
                    </pre>
                  ) : null}
                  {entry.result.exitCode !== 0 || entry.result.timedOut || entry.result.truncated ? (
                    <div className="sbx-term__meta">
                      {entry.result.timedOut ? <span>stopped at its timeout</span> : entry.result.exitCode !== 0 ? <span>exit {entry.result.exitCode}</span> : null}
                      {entry.result.truncated ? <span>output cut at 1 MiB</span> : null}
                    </div>
                  ) : null}
                </>
              ) : entry.error ? (
                <div className="sbx-term__pre sbx-term__err">
                  {entry.error.message}
                  {entry.error.fix ? <div className="sbx-term__meta">{entry.error.fix}</div> : null}
                </div>
              ) : (
                <Running entry={entry} />
              )}
            </div>
          ))
        )}
      </div>
      <form className="sbx-term__input" onSubmit={submit}>
        <span className="sbx-term__ps" aria-hidden="true">
          $
        </span>
        <label htmlFor="sbx-cmd" className="visually-hidden">
          Command
        </label>
        <input
          id="sbx-cmd"
          ref={input}
          value={cmd}
          placeholder={usable ? 'Type a command' : 'Closed'}
          onChange={(e) => setCmd(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'ArrowUp' && history.length) {
              e.preventDefault();
              const i = Math.min(history.length - 1, hIndex + 1);
              setHIndex(i);
              setCmd(history[i]);
            } else if (e.key === 'ArrowDown' && hIndex >= 0) {
              e.preventDefault();
              const i = hIndex - 1;
              setHIndex(i);
              setCmd(i >= 0 ? history[i] : '');
            }
          }}
          disabled={!usable}
          spellCheck={false}
          autoComplete="off"
          autoCapitalize="off"
        />
        <button type="submit" className="sbx-term__run" disabled={!usable || busy || !cmd.trim()} aria-busy={busy}>
          {busy ? <Spinner size={12} /> : <Play aria-hidden="true" />}
          Run
        </button>
      </form>
    </section>
  );
}

/* What people type, made into a name the plane takes: "iOS Build" is ios-build. */
const asName = (text: string): string =>
  text
    .trim()
    .toLowerCase()
    .replace(/[\s_]+/g, '-');

/** The page title: the sandbox's name, renamed in place. */
function SandboxTitle({ sandbox, onRenamed }: { sandbox: Sandbox; onRenamed: (s: Sandbox) => void }) {
  const [draft, setDraft] = useState<string | null>(null);
  const rename = useAction(async (name: string) => api<Sandbox>(`/v1/sandboxes/${encodeURIComponent(sandbox.id)}`, { method: 'PATCH', body: { name } }));
  const close = () => {
    setDraft(null);
    rename.setError(undefined);
  };
  async function save() {
    if (draft === null || rename.busy) return;
    const name = asName(draft);
    if (name === sandbox.name) return close();
    const updated = await rename.run(name);
    if (updated) {
      onRenamed(updated);
      close();
    }
  }

  if (draft === null) {
    return (
      <span className="sbx-title">
        <span className="sbx-title__name">{sandbox.name}</span>
        <button type="button" className="copy sbx-title__edit" aria-label="Rename" title="Rename" onClick={() => setDraft(sandbox.name)}>
          <Pencil aria-hidden="true" />
        </button>
      </span>
    );
  }
  return (
    <span className="sbx-rename">
      <span className="sbx-rename__row">
        <input
          className="input sbx-rename__input"
          aria-label="Sandbox name"
          value={draft}
          maxLength={48}
          autoFocus
          spellCheck={false}
          autoComplete="off"
          autoCapitalize="off"
          aria-invalid={rename.error ? true : undefined}
          aria-describedby={rename.error ? 'sbx-rename-error' : 'sbx-rename-hint'}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void save();
            if (e.key === 'Escape') close();
          }}
        />
        <button type="button" className="btn btn--primary btn--sm" disabled={rename.busy || !asName(draft)} aria-busy={rename.busy} onClick={() => void save()}>
          {rename.busy ? <Spinner size={12} /> : null}
          Save
        </button>
        <button type="button" className="btn btn--sm" onClick={close}>
          Cancel
        </button>
      </span>
      {rename.error ? (
        <span className="sbx-rename__error" id="sbx-rename-error" role="alert">
          {rename.error.code === 'name-taken' ? 'Another open sandbox already has this name.' : rename.error.code === 'bad-name' ? 'Use lowercase letters, digits and hyphens, up to 48.' : rename.error.message}
        </span>
      ) : (
        <span className="sbx-rename__hint" id="sbx-rename-hint">
          Lowercase letters, digits and hyphens. The id stays the same.
        </span>
      )}
    </span>
  );
}

export function SandboxDetail() {
  const { id = '' } = useParams();
  const now = useNow(1000);
  const { reloadBilling } = useBilling();
  const res = useResource<Sandbox>(`/v1/sandboxes/${encodeURIComponent(id)}`, { interval: 3000 });
  useDocumentTitle(res.data?.name ?? shortId(id));
  const team = useResource<TeamDetail>('/v1/team');
  const s = res.data;
  const kill = useAction(async () => api<Sandbox>(`/v1/sandboxes/${encodeURIComponent(id)}`, { method: 'DELETE' }));
  const extend = useAction(async (timeoutMs: number) => api<Sandbox>(`/v1/sandboxes/${encodeURIComponent(id)}/timeout`, { method: 'POST', body: { timeoutMs } }));

  if (res.error && !s) {
    return (
      <>
        <PageHead title="Sandbox" crumbs={<Link to="/sandboxes">Sandboxes</Link>} />
        <div className="band">
          {res.error.status === 404 ? (
            <Empty title="No such sandbox">
              <p>
                <code>{id}</code> doesn’t exist on this team.
              </p>
              <Link className="btn" to="/sandboxes">
                Back to sandboxes
              </Link>
            </Empty>
          ) : (
            <ErrorCallout error={res.error} onRetry={() => void res.reload()} />
          )}
        </div>
      </>
    );
  }

  const maxTimeout = team.data?.limits.maxSandboxTimeoutMs ?? 24 * 3_600_000;
  // POST …/timeout sets the end to timeoutMs from the call, at most maxTimeout after creation.
  const latestEnd = s ? s.createdAt + maxTimeout : 0;
  const canExtend = s ? latestEnd - s.expiresAt > 1000 : false;
  const open = s !== undefined && s.state !== 'closed';

  return (
    <>
      <PageHead
        crumbs={<Link to="/sandboxes">Sandboxes</Link>}
        title={s ? <SandboxTitle sandbox={s} onRenamed={res.setData} /> : 'Sandbox'}
        lede={
          s ? (
            <span className="sbx-meta">
              <StatePill state={s.state} />
              {s.state === 'closed' ? (
                <span>{s.closeReason ? CLOSE_REASON[s.closeReason] ?? s.closeReason : 'Closed'}</span>
              ) : s.state === 'pending' ? (
                <span>Waiting for a Mac</span>
              ) : (
                <span title={fullTime(s.expiresAt)}>{inFuture(s.expiresAt, now).replace(/^in /, '')} left</span>
              )}
              <span>{moneyFine(s.costUsd)}</span>
            </span>
          ) : undefined
        }
        actions={
          open || s?.replayUrl ? (
            <>
              {s?.replayUrl ? <ReplayLink id={s.id} live={open} /> : null}
              {open ? (
                <>
                  <button
                    type="button"
                    className="btn"
                    disabled={extend.busy || !canExtend}
                    aria-busy={extend.busy}
                    onClick={async () => {
                      const at = Date.now();
                      const end = Math.min(latestEnd, s.expiresAt + 30 * 60_000);
                      const updated = await extend.run(Math.max(1000, Math.floor(end - at)));
                      if (updated) {
                        res.setData(updated);
                        void reloadBilling();
                      }
                    }}
                    title={canExtend ? `Moves its end 30 minutes later, up to ${span(maxTimeout)} after it was created` : `A sandbox lives at most ${span(maxTimeout)} from creation`}
                  >
                    {extend.busy ? <Spinner /> : <Clock aria-hidden="true" />}
                    Add 30 min
                  </button>
                  <InlineConfirm
                    size=""
                    triggerClass="btn btn--danger"
                    trigger="Kill sandbox"
                    question="Kill it? Running commands stop and its files are deleted."
                    confirmLabel="Kill"
                    busy={kill.busy}
                    onConfirm={async () => {
                      const closed = await kill.run();
                      if (closed) {
                        res.setData(closed);
                        void reloadBilling();
                      }
                    }}
                  />
                </>
              ) : null}
            </>
          ) : undefined
        }
      />
      <div className="sbx">
        {kill.error || extend.error ? <ErrorCallout error={(kill.error ?? extend.error)!} /> : null}
        {s ? <CommandConsole sandbox={s} onRan={() => void res.reload()} /> : <span className="skeleton" style={{ height: 420, borderRadius: 10 }} />}
      </div>
    </>
  );
}
